import pathToRegexp from 'path-to-regexp';
import { PATH as SCREENING_PATH } from '@zg-rentals/screening-js/lib/constants/paths';
import { MISSING_PERMISSIONS } from './paths';

export const URLS = {
  ROOT_URL: 'rental-manager/properties/:propertyId',
  APPLICATIONS: 'applications',
  LANDLORD_REVIEW: 'landlord-review/:applicationId',
  LANDLORD_REVIEW_ROOT: 'landlord-review',
  ID_VERIFICATION: 'id-verification',
  APPLICANT: 'applicant(s)?/:zgAuthZone/:applicantId',
  RENTAL_APPLICATION: 'rental-application',
  SCREENING_CREDIT_REPORT: SCREENING_PATH.CREDITREPORT,
  SCREENING_BACKGROUND_CHECK: SCREENING_PATH.BACKGROUNDCHECK,
  SCREENING_LANDLORD_JUNCTION: SCREENING_PATH.LANDLORDJUNCTION,
  SCREENING_PREFIX: SCREENING_PATH.SCREENING_PREFIX,
  SCREENING: 'screening',
  MARKETING_LANDING_PAGE: 'https://www.zillow.com/z/rental-manager/tenant-screening/',
  TERMS_OF_USE: 'https://www.zillowgroup.com/terms-of-use/',
  RENTAL_USER_TERMS: 'https://www.zillow.com/renter-hub/terms/Rental-User-Terms',
  ADVERSE_ACTION: 'https://www.zillow.com/rental-manager/resources/fair-housing-guide',
  RESPECTFUL_RENTING_PLEDGE: 'https://www.zillow.com/corp/respectful-renting-pledge/',
  PROXY_PATH: 'rental-manager/applications/proxy',
  PONTOON_PROXY_PATH: 'rental-manager/applications/pontoon-proxy',
  NEW_ZENDESK_REQUEST: 'https://www.zillow.com/rental-manager/submit-request',
  FAQS: 'https://help.zillowrentalmanager.com/hc/en-us/categories/115002159348-Rental-Applications',
  SAMPLE_ONLINE_APPLICATION:
    'https://wp-tid.zillowstatic.com/bedrock/app/uploads/sites/2/2020/12/Sample-report-e66a3d.png',
  SAMPLE_BACKGROUND_CHECK:
    'https://wp-tid.zillowstatic.com/bedrock/app/uploads/sites/2/2021/12/CIC-Report-810-2x-for-Marketing-2dca38.png',
  SAMPLE_CREDIT_REPORT: 'https://wp.zillowstatic.com/26/credit-report-sample-b90c6f.png',
  ZRM: 'https://www.zillow.com/rental-manager/',
  R4R_RESTRICTION_HELP: 'https://help.zillowrentalmanager.com/hc/en-us/articles/360063237993',
  UNAVAILABLE_BACKGROUND_REPORT_FAQ:
    'https://zillow.zendesk.com/hc/en-us/articles/360026787753-Why-does-my-background-report-show-unavailable-',
  RENT_GUARANTEE_INCOME_DOCS_FAQ: 'https://help.zillowrentalmanager.com/hc/en-us/articles/5195174006419',
  VOUCHER_FAQ_LINK: 'https://www.zillow.com/rentals-network/housing-choice-vouchers/',
  RENT_GUARANTEE_INCOME_VERIFICATION_FAQ: 'https://help.zillowrentalmanager.com/hc/en-us/articles/5195153624212',
  COMPLIANCE_LEARN_MORE: 'https://www.zillow.com/z/rental-manager/limits-rental-applications-reports/',
  DECLINE_APPLICATION: 'decline',
  ZILLOW_ACCOUNT_PROFILE: '/myzillow/profile/',
  HUD: 'https://www.zillow.com/rental-manager/limits-rental-applications-reports',
  FTC_CONSUMER_REPORTS:
    'https://www.ftc.gov/business-guidance/blog/2016/11/screening-tenants-check-out-ftcs-new-guidance',
  GOOD_NEIGHBOR_POLICY: 'https://www.zillow.com/z/info/good-neighbor-policy/',
  LANDLORD_SCREENING_FAQ: 'https://help.zillowrentalmanager.com/hc/en-us/sections/360004966913-Screening-reports',
  PRIVACY_POLICY:
    'https://www.zillowgroup.com/privacy-policy/#:~:text=We%20partner%20with%20Experian%20and,store%20your%20payment%20account%20details',
  CONDITIONAL_APPROVAL_URL: 'https://zillow.zendesk.com/hc/en-us/articles/34495429077395-Conditional-Approval-FAQs',
};

export const CUSTOMER_SUPPORT_LINK = 'https://www.zillow.com/rental-manager/submit-request';
export const EXPERIAN_IDENTITY_MANAGEMENT_URL = 'https://www.experian.com/decision-analytics/identity-management';

const toNewLandlordReviewPath = pathToRegexp.compile('/rental-manager/applications/:applicationId');

export function getLandlordReviewPath(listingAlias: string, applicationId: string) {
  return `${toNewLandlordReviewPath({ applicationId })}?propertyId=${listingAlias}`;
}

export function getApplicationsTabPath(listingAlias: string) {
  return `/rental-manager/applications?propertyId=${listingAlias}`;
}

export function getIdVerificationPath(listingAlias: string) {
  return `/rental-manager/applications/${URLS.ID_VERIFICATION}?propertyId=${listingAlias}`;
}

export function getDeclineApplicationPath(applicationId: string, listingAlias: string) {
  return `/rental-manager/applications/${applicationId}/decline?propertyId=${listingAlias}`;
}

export function getApplicantPath({
  propertyId,
  applicationId,
  zgAuthZone,
  applicantId,
  pageLocation,
}: {
  propertyId: string;
  applicationId: string;
  zgAuthZone: string;
  applicantId: string;
  pageLocation?: string;
}) {
  return `/rental-manager/applications/${applicationId}/applicants/${zgAuthZone.toLowerCase()}/${applicantId}${
    pageLocation ? `/${pageLocation}` : ''
  }?propertyId=${propertyId}`;
}

export function getLeasesPath(listingAlias: string, applicationId: string) {
  return `/rental-manager/leasing/property/${listingAlias}/onboarding/createLease?applicationId=${applicationId}`;
}

export function getPaymentsPath(listingAlias: string) {
  return `/rental-manager/payments/properties/${listingAlias}`;
}

export function getGenericApplicationUrl(host: string, listingAlias: string) {
  return `https://${host}/renter-hub/applications/listing/${listingAlias}/rental-application?utm_source=applicationshare&utm_medium=desktopmweb&utm_campaign=applicationlinkshare`;
}

export function getMessageConversationPath(listingAlias: string, conversationId: string) {
  return `/rental-manager/inbox/${listingAlias}/${conversationId}`;
}

export function getRentalProtectionManagePolicyPath(listingAlias: string) {
  return `/rental-manager/rental-protection/properties/${listingAlias}/managePolicy`;
}

export function getRentalProtectionOptInPath(listingAlias: string) {
  return `/rental-manager/rental-protection/properties/${listingAlias}`;
}

export function getMissingPermissionsPath(listingAlias: string) {
  return `${MISSING_PERMISSIONS}?propertyId=${listingAlias}`;
}

export function getPrintApplicationPath(listingAlias: string, applicationId: string) {
  return `/rental-manager/applications/pdfs/rental-application.pdf?applicationId=${applicationId}&propertyId=${listingAlias}`;
}
